import { CoreArchiveDialog } from "@/components/core-archive-dialog";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Search, Download, Filter, ArrowUpDown, X, Edit, Archive, ExternalLink } from "lucide-react";
import type { Contact, ClientGroup } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ContactFormModal } from "@/components/contact-form-modal";
import { useUrlState } from "@/hooks/use-url-state";

type SortField = "name" | "email" | "mobile" | "createdAt";
type SortDirection = "asc" | "desc";

export default function ContactList() {
  const [, navigate] = useLocation();

  // Use URL state for filter persistence
  // Search uses debouncing to prevent focus loss on every keystroke
  const [status, setStatus] = useUrlState("status", "active");
  const [searchTerm, setSearchTerm] = useUrlState("search", "", { debounceMs: 300 });
  const [clientGroupId, setClientGroupId] = useUrlState("clientGroup", "");
  const [sortField, setSortField] = useUrlState<SortField>("sort", "createdAt");
  const [sortDirection, setSortDirection] = useUrlState<SortDirection>("dir", "desc");

  // Local state (not persisted in URL)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [contactToArchive, setContactToArchive] = useState<Contact | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  // Fetch client groups for filter
  const { data: clientGroups } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // Fetch contacts with filters (uses default queryFn with auth headers)
  const { data, isLoading } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", {
      status,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(clientGroupId ? { clientGroupId } : {}),
    }],
  });

  // Client-side sorting
  const displayContacts = (data || []).sort((a, b) => {
    let aValue: string | Date;
    let bValue: string | Date;

    switch (sortField) {
      case "name":
        aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
        bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
        break;
      case "email":
        aValue = (a.email || "").toLowerCase();
        bValue = (b.email || "").toLowerCase();
        break;
      case "mobile":
        aValue = (a.mobile || "").toLowerCase();
        bValue = (b.mobile || "").toLowerCase();
        break;
      case "createdAt":
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setClientGroupId("");
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleArchive = (contact: Contact) => {
    setContactToArchive(contact);
    setArchiveDialogOpen(true);
  };

  const handleView = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };



  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Contacts"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Contacts" }]}
        actions={
          <Button 
            onClick={() => setIsModalOpen(true)}
            data-testid="button-add-contact"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Contact
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Search and Filters */}
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or mobile..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-contacts"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger aria-label="Contact status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-filter-contacts"
                      className={clientGroupId ? "bg-accent" : ""}
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      Filters {clientGroupId && "(1)"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" align="end">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Filter by Client Group</h4>
                        <Select value={clientGroupId} onValueChange={setClientGroupId}>
                          <SelectTrigger>
                            <SelectValue placeholder="All client groups" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value=" ">All client groups</SelectItem>
                            {clientGroups?.map((group) => (
                              <SelectItem key={group.id} value={group.id}>
                                {group.groupName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFilters}
                          className="flex-1"
                        >
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setIsFilterOpen(false)}
                          className="flex-1"
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" size="sm" data-testid="button-export-contacts">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          {/* Contacts Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="h-10">
                  <TableHead className="px-2 py-1 text-xs font-medium">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 hover:bg-transparent"
                      onClick={() => toggleSort("name")}
                    >
                      Name
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 hover:bg-transparent"
                      onClick={() => toggleSort("email")}
                    >
                      Email
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 hover:bg-transparent"
                      onClick={() => toggleSort("mobile")}
                    >
                      Mobile
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Type</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Client Group</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 hover:bg-transparent"
                      onClick={() => toggleSort("createdAt")}
                    >
                      Created
                      <ArrowUpDown className="ml-2 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium text-right w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="px-2 py-8 text-center text-muted-foreground">
                      Loading contacts...
                    </TableCell>
                  </TableRow>
                ) : displayContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="px-2 py-8 text-center text-muted-foreground">
                      No contacts found. {searchTerm && "Try adjusting your search."}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayContacts.map((contact) => {
                    const clientGroup = clientGroups?.find(g => g.id === contact.clientGroupId);
                    return (
                      <TableRow
                        key={contact.id}
                        className="h-10 hover:bg-muted/50"
                        data-testid={`row-contact-${contact.id}`}
                      >
                        <TableCell className="px-2 py-1 text-sm font-medium">
                          <span>
                            {contact.firstName} {contact.lastName}
                          </span>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">{contact.email || "-"}</TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">{contact.mobile || "-"}</TableCell>
                        <TableCell className="px-2 py-1">
                          <Badge variant="secondary" className="text-xs capitalize">
                            {contact.contactType?.replace("_", " ") || "individual"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {clientGroup?.groupName || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString("en-AU") : "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(contact)}
                              data-testid={`button-edit-contact-${contact.id}`}
                              title="Edit contact"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(contact)}
                              data-testid={`button-archive-contact-${contact.id}`}
                              title={contact.archivedAt ? "Restore contact" : "Archive contact"}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(contact.id)}
                              data-testid={`button-view-contact-${contact.id}`}
                              title="View details"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>Showing {displayContacts.length} contact{displayContacts.length !== 1 ? "s" : ""}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled data-testid="button-previous-page">
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled data-testid="button-next-page">
                Next
              </Button>
            </div>
          </div>
        </div>
      </main>

      <ContactFormModal
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) {
            setEditingContact(undefined);
          }
        }}
        contact={editingContact}
      />

      <CoreArchiveDialog entity="contact" record={contactToArchive} open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen} onSuccess={() => setContactToArchive(null)} />
    </div>
  );
}
